/**
 * Card "Contagem regressiva" da Home antes da viagem — layout da referência
 * do PO (teste físico, item 25): meses · dias · horas · minutos, a partida,
 * o avião e as nuvens (`design/background_card_flight.png`, `icon_flight.png`).
 *
 * A hora da partida é a da 1ª atividade do 1º dia (PO, 12/10/2026: o
 * embarque às 22:15 entra no roteiro como atividade). Sem atividade com hora,
 * a contagem vai até a meia-noite do 1º dia e a linha mostra só a data.
 * Sempre no fuso da viagem.
 * Atualiza a cada minuto enquanto a tela está aberta.
 */
import { countdownTo, formatDayMonth, localToInstant, type TripDates } from '@nv/core';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';


function Bloco({ valor, rotulo }: { valor: number; rotulo: string }) {
  return (
    <span className="cont__bloco">
      <span className="cont__valor">{String(valor).padStart(2, '0')}</span>
      <span className="cont__rotulo">{rotulo}</span>
    </span>
  );
}

export function ContagemRegressiva({ viagem, agora, hora = null }: { viagem: TripDates; agora: Date; hora?: string | null }) {
  const navegar = useNavigate();
  // O relógio anda sozinho; `agora` vem de fora só para o painel de
  // demonstração conseguir simular outra data.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 60_000);
    return () => window.clearInterval(t);
  }, []);
  const instante = (() => {
    try {
      return localToInstant(viagem.timeZone, viagem.startDate, hora ?? '00:00');
    } catch {
      return null;
    }
  })();
  if (instante === null) return null;
  const c = countdownTo(instante, agora.getTime() + tick * 60_000);

  return (
    <section className="cont" aria-label="Contagem regressiva" data-testid="contagem-regressiva">
      <img className="cont__fundo nv-float" src="/ilustracoes/background-card-flight.png" alt="" aria-hidden="true" width={101} height={91} />
      <span className="cont__kicker">CONTAGEM REGRESSIVA</span>
      {/* Duas linhas equilibradas: o "em" nunca fica sozinho (refino final). */}
      <h2 className="cont__titulo">
        Sua viagem
        <br />
        {c.chegou ? 'começa hoje' : 'começa em'}
      </h2>
      {!c.chegou && (
        <div className="cont__blocos">
          {c.meses > 0 && <Bloco valor={c.meses} rotulo={c.meses === 1 ? 'mês' : 'meses'} />}
          <Bloco valor={c.dias} rotulo={c.dias === 1 ? 'dia' : 'dias'} />
          <Bloco valor={c.horas} rotulo="horas" />
          <Bloco valor={c.minutos} rotulo="min" />
        </div>
      )}
      <div className="cont__rodape">
        <span className="cont__partida">
          <span className="cont__partida-linha">
            <img src="/ilustracoes/icon-flight.png" alt="" aria-hidden="true" width={21} height={15} />
            Partida: {formatDayMonth(viagem.startDate)}
            {hora && ` às ${hora}`}
          </span>
          <span className="cont__dica">Prepare malas, documentos e roteiro!</span>
        </span>
        <button type="button" className="nv-btn nv-btn--primary cont__ver" onClick={() => navegar('/roteiro')}>
          Ver detalhes
        </button>
      </div>
    </section>
  );
}
